// 2026-09-01 - FormuLearning - COMP-FORMULATION-NAMING (이슈 #58 S10/#70, M84-60)
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 접두어 입력·저장 비활성.
// 파일 위치: frontend/src/features/step4/FormulationNaming.tsx
// 목적: 처방 연번의 **접두어**를 사용자가 정한다(BR-RND-01). 이후 생성되는 처방이
//       `AMBO-01`·`-02` … 로 명명된다. 연번은 initial·optimization 을 구분하지 않고
//       프로젝트 안에서 이어 증가한다.
//
// 화면 규약 셋.
//   ① 위치는 Step 4 **가장 첫 부분**이다(UI/UX v2.2 §4.7 §4.0) — `Initial Design
//      Algorithm` 위. 처방이 만들어지기 전에 이름을 정하는 것이 순서다.
//   ② **왜 입력받는지**를 적는다. 원료·연구자에 따라 명명 관행이 다르고(Fenofibrate →
//      `FN-01`) 시스템이 정하면 연구 노트와 어긋난다.
//   ③ 저장 후 **기존 배치 이름이 그대로임을 안내**한다(BR-S4-L13 — 부여 시점 고정).
//      표의 이름이 안 바뀌면 사용자는 저장이 실패한 것으로 읽는다. 서버가 그 건수를
//      `unchanged_batches` 로 실어 보내는 이유다.

import { useState } from 'react'
import { apiErrorDetail } from '../../api/lot'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

/** 접두어 미설정 시 서버가 쓰는 잠정값(`round_service.DEFAULT_PREFIX`). */
const DEFAULT_PREFIX = 'FM'

interface Props {
  /** 저장된 접두어. 미설정이면 `null`·빈 문자열이며 서버 잠정값이 쓰인다. */
  prefix: string | null
  /** 저장 → 서버가 되돌려 준 확정 접두어와 이름이 바뀌지 않는 기존 배치 수. */
  onSave: (prefix: string) => Promise<{ formulation_prefix: string; unchanged_batches: number }>
}

export default function FormulationNaming({ prefix, onSave }: Props) {
  const canWrite = useCanWrite()
  const roTitle = canWrite ? undefined : READ_ONLY_TITLE
  const saved = (prefix ?? '').trim()
  const [value, setValue] = useState(saved)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 미리보기는 **입력 중인 값**을 따른다 — 저장 전에 결과를 보여야 오타를 그 자리에서 잡는다.
  const effective = value.trim() || saved || DEFAULT_PREFIX
  const dirty = value.trim() !== saved

  const handleSave = async () => {
    const next = value.trim()
    if (!next || saving) return
    setSaving(true)
    setNotice(null)
    setError(null)
    try {
      const res = await onSave(next)
      setValue(res.formulation_prefix)
      setNotice(
        `접두어를 '${res.formulation_prefix}' 로 저장했습니다. 다음 처방부터 `
        + `${res.formulation_prefix}-NN 으로 명명됩니다.`
        + (res.unchanged_batches > 0
          ? ` 이미 생성된 처방 ${res.unchanged_batches}건의 이름은 그대로입니다`
            + ' — 식별자는 부여 시점에 고정됩니다.'
          : ''),
      )
    } catch (e) {
      // 422(빈 값·16자 초과)는 서버 사유를 그대로 보인다 — 조용히 삼키면 무엇을
      // 고쳐야 하는지 모른 채 버튼만 다시 누른다(조성·결과 저장과 같은 규약).
      setError(apiErrorDetail(e) ?? '접두어 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mb-4" data-testid="formulation-naming">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
        Formulation Naming
      </h4>
      <div className="rounded border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] text-text-secondary">Prefix:</span>
          <input
            type="text"
            value={value}
            maxLength={16}
            placeholder={saved || DEFAULT_PREFIX}
            onChange={(e) => { setValue(e.target.value); setNotice(null); setError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSave() }}
            data-testid="formulation-prefix-input"
            disabled={!canWrite}
            title={roTitle}
            className="w-32 rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
            style={{ border: '1px solid var(--border-soft)' }}
          />
          <span className="text-[11px] text-text-muted" data-testid="formulation-prefix-preview">
            → {effective}-01, {effective}-02, …
          </span>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !value.trim() || !dirty || !canWrite}
            title={roTitle}
            data-testid="formulation-prefix-save"
            className="rounded border px-2.5 py-1 text-[10px] font-semibold text-text-body transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>

        <p className="mt-2 text-[10px] text-text-muted">
          처방 명명 접두어입니다. 원료·연구자에 따라 관행이 다릅니다(Fenofibrate → FN-01).
          연번은 초기 설계와 최적화를 구분하지 않고 이어서 증가합니다.
        </p>

        {notice && (
          <p className="mt-2 text-[10px] text-accent-teal" data-testid="formulation-prefix-notice">
            {notice}
          </p>
        )}
        {error && (
          <p className="mt-2 text-[10px] text-warn" data-testid="formulation-prefix-error">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
